import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/system";
import {
  Paper, Typography, Chip, Tooltip, Avatar, Stack, Link,
  LinearProgress, Divider, Button, FormControlLabel, Switch,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Alert,
  Checkbox, Table, TableBody, TableCell, TableHead, TableRow, IconButton, Accordion, AccordionSummary, AccordionDetails,
  ToggleButtonGroup, ToggleButton, Grid, CircularProgress
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SettingsIcon from "@mui/icons-material/Settings";
import RefreshIcon from "@mui/icons-material/Refresh";
import PaymentsIcon from "@mui/icons-material/Payments";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import PriceCheckIcon from "@mui/icons-material/PriceCheck";
import PercentIcon from "@mui/icons-material/Percent";
import HistoryIcon from "@mui/icons-material/History";
import PersonIcon from "@mui/icons-material/Person";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import axios from "axios";
import {
  getCommissionSummary,
  getCommissionHistory,
  approveCommission,
  payCommission,
  createManualCommission,
  regenerateCommission,
  updateCommissionPayout,
} from "../../services/commissions"; // <-- adjust path if needed
import { fetchLatestCompletedSalesOrderForOrder } from "../../services/estimation.js";

/* -------------------- helpers (unchanged) -------------------- */
function currency(n) { return `CA$${(Number(n) || 0).toFixed(2)}`; }
const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z]/g, "");
const hasRole = (u, target) => {
  const needle = norm(target);
  const flat = [].concat(u?.role ?? []).concat(u?.roles ?? []);
  return flat.some((r) => norm(r) === needle);
};
const getUserAvatar = (user) => {
  const fallback = "/elite-core-placeholder-avatar.png";
  if (!user) return fallback;
  let profilePic = user.profilePicture || user.avatar || user.profile_image || user.picture || null;
  if (typeof profilePic === "string") { try { profilePic = JSON.parse(profilePic); } catch {} }
  if (Array.isArray(profilePic) && profilePic.length > 0) {
    const selected = profilePic.find((p) => p?.selected) || profilePic[0];
    const url = selected?.url;
    if (url) return url.startsWith("uploads")
      ? `${process.env.REACT_APP_NODEJS_SERVER_URL}/${url}` : url;
  }
  if (typeof profilePic === "string") {
    return profilePic.startsWith("uploads")
      ? `${process.env.REACT_APP_NODEJS_SERVER_URL}/${profilePic}` : profilePic;
  }
  return fallback;
};
const getEmail = (u = {}) => {
  const direct = u.email || u.work_email || u.personal_email || u.user_email || u.emailAddress || u.contact_email;
  if (direct) return direct;
  const nested = u.user ? getEmail(u.user) : null;
  if (nested) return nested;
  for (const [k, v] of Object.entries(u)) {
    if (k.toLowerCase().includes("email") && typeof v === "string" && v.includes("@")) return v;
  }
  return null;
};
const disabledContextFieldSx = {
  '& .MuiInputBase-input.Mui-disabled': {
    WebkitTextFillColor: 'rgba(0, 0, 0, 0.65)',
  },
  '& .MuiOutlinedInput-root.Mui-disabled': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
};

const getName = (u = {}) => {
  const first = u.first_name ?? u.firstName ?? u.given_name ?? "";
  const last = u.last_name ?? u.lastName ?? u.family_name ?? "";
  const combined = `${first} ${last}`.trim();
  if (combined) return combined;
  if (u.name && typeof u.name === "string") return u.name;
  if (u.user && typeof u.user === "object") return getName(u.user);
  const email = getEmail(u);
  if (email) return email.split("@")[0];
  return "";
};
const mergeFromDirectory = (assigned, directory = []) => {
  const base = assigned?.user ? { ...assigned, ...assigned.user } : assigned;
  const dirMatch = directory.find((d) => String(d.id) === String(base?.id));
  return dirMatch ? { ...base, ...dirMatch } : base;
};

const ASSIGNMENT_MODES = {
  clientOrder: "clientOrder",
  clientOnly: "clientOnly",
  orderOnly: "orderOnly",
  allAssigned: "allAssigned",
};

const ASSIGNMENT_MODE_OPTIONS = [
  { value: ASSIGNMENT_MODES.clientOrder, label: "Client + Order Rep" },
  { value: ASSIGNMENT_MODES.clientOnly, label: "Client Rep Only" },
  { value: ASSIGNMENT_MODES.orderOnly, label: "Order Rep Only" },
  { value: ASSIGNMENT_MODES.allAssigned, label: "All Assigned Reps" },
];

/* -------------------- tiers -------------------- */
const DEFAULT_TIERS = [
  { min: 0.20, max: 0.25, rate: 0.03 },
  { min: 0.26, max: 0.30, rate: 0.04 },
  { min: 0.31, max: 0.40, rate: 0.05 },
  { min: 0.41, max: null,  rate: 0.06 },
];
const resolveTier = (marginFrac, tiers = DEFAULT_TIERS) => {
  const m = Math.max(0, Number(marginFrac) || 0);
  const list = [...tiers].sort((a, b) => (a.min ?? 0) - (b.min ?? 0));
  return list.find(t => m >= (t.min ?? 0) && (t.max == null ? true : m <= t.max)) || null;
};

function KpiCard({ label, value, icon: Icon, iconColor = "primary.main", highlight = false }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        borderRadius: 2,
        borderTop: highlight ? "3px solid" : "1px solid",
        borderTopColor: highlight ? "primary.main" : "divider",
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: 2 },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          bgcolor: "action.hover",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon sx={{ color: iconColor, fontSize: 20 }} />
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.2 }}>
          {label}
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: "monospace", lineHeight: 1.4 }}>
          {value}
        </Typography>
      </Box>
    </Paper>
  );
}

function InfoRow({ label, value, badge, tooltip, last = false }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: 0.75,
        px: 1,
        borderBottom: last ? "none" : "1px solid",
        borderBottomColor: "divider",
        "&:hover": { bgcolor: "action.hover", borderRadius: 1 },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        {tooltip && (
          <Tooltip title={tooltip} arrow>
            <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
          </Tooltip>
        )}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: "monospace" }}>{value}</Typography>
        {badge}
      </Box>
    </Box>
  );
}

function SectionCard({ title, icon: Icon, action, children, sx = {} }) {
  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", mb: 2, ...sx }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.25,
          bgcolor: "action.hover",
          borderBottom: "1px solid",
          borderBottomColor: "divider",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {Icon && <Icon sx={{ fontSize: 18, color: "text.secondary" }} />}
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{title}</Typography>
        </Box>
        {action && <Box>{action}</Box>}
      </Box>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Paper>
  );
}
/* ---------------------------------------------------------------------------------- */
/**
 * Props:
 * - orderId?: number                      // if provided, component will fetch summary & history
 * - canFinance?: boolean                  // show Approve / Mark Paid
 * - bookedMargin, billedActual, targetBooked, invoicedTotal,
 *   commissionEligible, bookedRevenue, commissionTiers, payoutBasis,
 *   assignedUsers, userDirectory
 */
export default function Commissions({
  orderId,
  instanceUniqueId,
  canFinance = false,
  isAdministrator = false,

  bookedMargin: _bookedMargin = 0,
  billedActual: _billedActual = 0,
  targetBooked: _targetBooked = 0,
  invoicedTotal: _invoicedTotal = 0,
  commissionEligible,
  bookedRevenue,
  commissionTiers = DEFAULT_TIERS,
  payoutBasis = "revenue",
  assignedUsers = [],
  userDirectory = [],
}) {
  // local summary (from server if orderId present; otherwise derive from props)
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(Boolean(orderId));
  const [completedSO, setCompletedSO] = useState(null);

  // dialogs
  const [payOpen, setPayOpen] = useState(false);
  const [approveBusy, setApproveBusy] = useState(false);
  const [payBusy, setPayBusy] = useState(false);
  const [payForm, setPayForm] = useState({ amount: "", reference: "", paidAt: "", kind: "payment", repUserId: "" });
  const [showActions, setShowActions] = useState(false);
  const SHOW_LEGACY_TOP_METRICS = false;
  const SHOW_LEGACY_PAYOUT_SUMMARY = false;
  const SHOW_LEGACY_ACTUAL_TOGGLE = false;
  const SHOW_REGENERATE_COMMISSION_BUTTON = false;
  const [historyView, setHistoryView] = useState("records");
  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [amountDirty, setAmountDirty] = useState(false);
  const [editingCommissionId, setEditingCommissionId] = useState(null);
  const [commissionModalMode, setCommissionModalMode] = useState("create"); // create | edit
  const [savingRecipients, setSavingRecipients] = useState(false);
  const [commissionRates, setCommissionRates] = useState([]);
  const [commissionAssignments, setCommissionAssignments] = useState({});
  const [commissionMarginTable, setCommissionMarginTable] = useState([]);
  const [assignmentMode, setAssignmentMode] = useState(ASSIGNMENT_MODES.clientOrder);
  const [recipientEdits, setRecipientEdits] = useState([]);
  const [stickyAssignedUsers, setStickyAssignedUsers] = useState([]);
  const [createForm, setCreateForm] = useState({
    commissionableSubtotal: "",
    marginAmount: "",
    repUserId: "",
    type: "orderRep",
    source: "margin",
    ratePercent: "",
    amount: "",
    note: "",
    paid: false,
    relatedInvoiceId: "",
    entryType: "manual",
  });

  // load summary/history
  useEffect(() => {
    let mounted = true;
    async function run() {
      if (!orderId) return;
      setLoading(true);
      try {
        const [s, h] = await Promise.all([
          getCommissionSummary(orderId),
          getCommissionHistory(orderId),
        ]);
        if (mounted) { setSummary(s); setHistory(h); }
      } catch (e) {
        console.error("Commission load failed", e);
        if (mounted) { setSummary(null); setHistory([]); }
      } finally {
        mounted && setLoading(false);
      }
    }
    run();
    return () => { mounted = false; };
  }, [orderId]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!orderId) return;
      const so = await fetchLatestCompletedSalesOrderForOrder(orderId);
      if (mounted) setCompletedSO(so || null);
    })();
    return () => { mounted = false; };
  }, [orderId]);

  const parseStamped = (raw) => {
    if (!raw) return null;
    if (typeof raw === "object") return raw;
    if (typeof raw === "string") {
      try { return JSON.parse(raw); } catch { return null; }
    }
    return null;
  };
  const stamped = parseStamped(summary?.stampedBookedAmount || completedSO?.stampedBookedAmount);

  // Fallback to props if no orderId
  const completedSoRevenue = Number(
    completedSO?.cart?.bookedRevenue ??
    completedSO?.Total ??
    completedSO?.total ??
    0
  );
  const bookedRevenueResolved = Number(
    bookedRevenue ??
    summary?.bookedRevenueSubtotal ??
    summary?.revenueSubtotal ??
    stamped?.targetRevenue ??
    completedSoRevenue
  );
  const bookedMargin = summary?.bookedMargin
    ?? _bookedMargin
    ?? Number(stamped?.bookedMargin ?? completedSO?.cart?.bookedMargin ?? 0);
  const billedActual = summary?.billedActual ?? _billedActual;
  const targetBooked = summary?.targetBooked ?? _targetBooked;
  const invoicedTotal = Math.max(
    0,
    Number(summary?.invoicedActualFromPaidInvoices ?? 0),
    Number(summary?.invoicedActual ?? 0),
    Number(_invoicedTotal ?? 0),
  );

  const billedNum = Math.max(0, Number(billedActual) || 0);
  const invoicedNum = Math.max(0, Number(invoicedTotal) || 0);

  const eligibleGate =
    commissionEligible != null
      ? Math.max(0, Number(commissionEligible) || 0)
      : Math.max(0, Math.min(billedNum, invoicedNum));

  const inferredRevenue = (Number(bookedMargin) || 0) + (Number(targetBooked) || 0);
  const revenue = Math.max(0, Number(
    Number.isFinite(bookedRevenueResolved) && bookedRevenueResolved > 0
      ? bookedRevenueResolved
      : inferredRevenue
  ));
  const marginFrac = summary?.marginFrac ?? (revenue > 0 ? Math.max(0, Math.min(1, (Number(bookedMargin) || 0) / revenue)) : 0);

  const activeTier = resolveTier(marginFrac, commissionTiers);
  // If we have a lock from server, prefer that rate
  const activeRate = summary?.lock ? Number(summary.lock.rate || 0) : (activeTier?.rate ?? 0);

  const [useSalesSnapshot, setUseSalesSnapshot] = useState(false);

  // rep
  const clientRepCandidates = (Array.isArray(assignedUsers) ? assignedUsers : [])
    .filter((u) => norm(u?.role) === "clientrep" || hasRole(u, "clientrep"));
  const repRaw = clientRepCandidates[0] || null;
  const rep = repRaw ? mergeFromDirectory(repRaw, userDirectory) : null;
  const fullName = rep ? getName(rep) : "";
  const email = rep ? getEmail(rep) : null;
  const avatarUrl = rep ? getUserAvatar(rep) : null;
  const canViewDebugInputs = Boolean(isAdministrator);

const repOptions = useMemo(() => {
  const list = Array.isArray(assignedUsers) && assignedUsers.length
    ? assignedUsers
    : stickyAssignedUsers;

  const mapped = list
    .map((u) => mergeFromDirectory(u, userDirectory))
    .filter(Boolean)
    .map((u) => {
      const roles = [];
      if (hasRole(u, "order-rep") || hasRole(u, "orderrep")) roles.push("Order Rep");
      if (hasRole(u, "client-rep") || hasRole(u, "clientrep")) roles.push("Client Rep");

      return {
        id: u.id,
        name: getName(u) || u.email || "Unnamed User",
        roles: roles.length ? roles.join(" / ") : "Rep",
      };
    })
    .filter((u) => u.id);

  const unique = new Map();
  mapped.forEach((u) => {
    if (!unique.has(String(u.id))) unique.set(String(u.id), u);
  });

  return Array.from(unique.values());
}, [assignedUsers, stickyAssignedUsers, userDirectory]);

const repNameById = useMemo(() => {
  const map = new Map();
  repOptions.forEach((r) => map.set(String(r.id), r.name));
  return map;
}, [repOptions]);

const recordEntryTypes = useMemo(
  () => new Set(["invoice_auto", "manual", "adjustment", "cost_true_up"]),
  []
);

const commissionRecords = useMemo(
  () =>
    (history || []).filter((row) =>
      recordEntryTypes.has(String(row?.entryType || "").toLowerCase())
    ),
  [history, recordEntryTypes]
);

const commissionAuditHistory = useMemo(
  () =>
    (history || []).filter(
      (row) => !recordEntryTypes.has(String(row?.entryType || "").toLowerCase())
    ),
  [history, recordEntryTypes]
);

const visibleCommissionRows =
  historyView === "records" ? commissionRecords : commissionAuditHistory;

  const relatedInvoiceOptions = useMemo(() => {
    const fromSummary = Array.isArray(summary?.relatedInvoices) ? summary.relatedInvoices : [];
    const byId = new Map();
    fromSummary.forEach((invoice) => {
      const id = Number(invoice?.invoiceId ?? invoice?.id ?? 0);
      if (!Number.isInteger(id) || id <= 0) return;
      byId.set(String(id), {
        id,
        invoiceId: id,
        invoiceNumber: invoice?.invoiceNumber || `INV-${id}`,
        amount: Number(invoice?.amount || 0),
        paymentStatus: invoice?.paymentStatus || null,
      });
    });
    (Array.isArray(history) ? history : []).forEach((row) => {
      const id = Number(row?.invoiceId || row?.snapshot?.relatedInvoiceId || 0);
      if (!Number.isInteger(id) || id <= 0 || byId.has(String(id))) return;
      byId.set(String(id), {
        id,
        invoiceId: id,
        invoiceNumber: row?.snapshot?.relatedInvoiceNumber || row?.invoiceNumber || `INV-${id}`,
        amount: Number(row?.invoiceTotal ?? 0),
        paymentStatus: row?.paymentStatus || null,
      });
    });
    return Array.from(byId.values()).sort((a, b) => Number(b.id) - Number(a.id));
  }, [history, summary?.relatedInvoices]);
  const defaultRelatedInvoiceId = relatedInvoiceOptions.length === 1 ? String(relatedInvoiceOptions[0].id) : "";
  const isRecordRowPaid = (row = {}) => {
    const balance = Number(row?.balance ?? row?.balanceRemaining ?? 0);
    const paidToDate = Number(row?.paidToDate ?? 0);
    return row?.isPaid === true
      || String(row?.paymentStatus || "").toLowerCase() === "paid"
      || Boolean(row?.paidAt || row?.isPosted)
      || (Math.abs(balance) < 0.01 && Math.abs(paidToDate) > 0.009);
  };
  const isRecordRowPartiallyPaid = (row = {}) => {
    if (isRecordRowPaid(row)) return false;
    if (String(row?.paymentStatus || "").toLowerCase() === "partial") return true;
    return Math.abs(Number(row?.paidToDate ?? 0)) > 0.009;
  };
  const getRecordPaidLabel = (row = {}) => {
    if (isRecordRowPaid(row)) return "Paid";
    if (isRecordRowPartiallyPaid(row)) return "Partial";
    return "No";
  };
  const getHistoryStatusLabel = (row = {}) => {
    const rowType = String(row?.entryType || "").toLowerCase();
    if (rowType === "payment") {
      if (row?.voidedAt) return "Reversed";
      if (row?.isPosted || row?.paidAt) return "Paid";
      return "Draft / Unpaid";
    }
    if (row?.voidedAt) return "Voided";
    if (row?.paidAt || row?.isPosted) return "Paid";
    return "Draft / Unpaid";
  };

  const openEditCommission = (entry) => {
    if (!entry) return;
    const kind = String(entry.entryType || "").toLowerCase();
    if (!["manual", "invoice_auto"].includes(kind)) return;

    const snapshot = entry?.snapshot && typeof entry.snapshot === 'object' ? entry.snapshot : {};
    const calcTotals = snapshot.calculatorTotals && typeof snapshot.calculatorTotals === "object"
      ? snapshot.calculatorTotals
      : {};
    const recipient = snapshot.recipient && typeof snapshot.recipient === "object"
      ? snapshot.recipient
      : {};
    const supportedBasis = (value, fallback = "margin") => {
      const v = String(value || "").toLowerCase();
      return v === "subtotal" || v === "margin" ? v : fallback;
    };

    if (kind === "manual" || entry?.isManualCommission === true) {
      const adjustmentMode = snapshot.adjustmentMode || snapshot.amountMode;
      const isManualFlat =
        adjustmentMode === "manual_amount" ||
        snapshot.amountMode === "manual" ||
        snapshot.amountWasManuallyEdited === true ||
        entry?.amountWasManuallyEdited === true ||
        Number(entry?.rate ?? snapshot.rate ?? 0) === 0;
      const manualAmount = Number(
        entry?.amount ??
        entry?.eventAmount ??
        snapshot.amount ??
        snapshot.eventAmount ??
        0
      );
      const manualRate = Number(entry?.rate ?? snapshot.rate ?? 0);
      const manualSource = supportedBasis(
        entry?.basis ||
        snapshot.source ||
        snapshot.basis ||
        "margin",
        "margin"
      );
      const manualType = snapshot.type || entry?.type || "clientRep";
      const manualNote = entry?.note || snapshot.reason || snapshot.note || "";
      const manualCommissionableSubtotal = Number(
        snapshot.commissionableSubtotal ??
        entry?.commissionableSubtotal ??
        summary?.commissionableSubtotal ??
        0
      );
      const manualMarginAmount = Number(
        snapshot.marginAmount ??
        entry?.marginAmount ??
        summary?.actualMargin ??
        0
      );

      setCreateForm({
        commissionableSubtotal: Number.isFinite(manualCommissionableSubtotal) ? manualCommissionableSubtotal.toFixed(2) : "0.00",
        marginAmount: Number.isFinite(manualMarginAmount) ? manualMarginAmount.toFixed(2) : "0.00",
        repUserId: entry?.repUserId ? String(entry.repUserId) : String(snapshot.repUserId || recipient.repUserId || ""),
        type: manualType,
        source: manualSource,
        ratePercent: Number.isFinite(manualRate) ? (manualRate * 100).toFixed(2) : "0.00",
        amount: Number.isFinite(manualAmount) ? manualAmount.toFixed(2) : "0.00",
        note: manualNote,
        paid: Boolean(entry?.derivedPaid || entry?.isPaid || entry?.paymentStatus === "paid" || entry?.paidAt || entry?.isPosted),
        relatedInvoiceId: entry?.invoiceId || snapshot.relatedInvoiceId ? String(entry?.invoiceId || snapshot.relatedInvoiceId) : "",
        entryType: "manual",
      });
      setAmountDirty(isManualFlat);
      setCreateOpen(true);
      setEditingCommissionId(entry.id);
      setCommissionModalMode("edit");
      return;
    }

    const sourceBasis = supportedBasis(
      recipient.basis ||
      snapshot.basis ||
      entry?.basis ||
      snapshot.overrideBasis,
      "margin"
    );

    const commissionableSubtotal = Number(
      snapshot.commissionableSubtotal ??
      calcTotals.revenueSubtotal ??
      calcTotals.fullOrderSubtotal ??
      calcTotals.paidNonDepositSubtotal ??
      entry?.commissionableSubtotal ??
      0
    );
    const marginAmount = Number(
      snapshot.marginAmount ??
      calcTotals.actualMargin ??
      recipient.commissionBase ??
      entry?.marginAmount ??
      0
    );
    const rate = Number(
      recipient.rate ??
      snapshot.rate ??
      entry?.rate ??
      0
    );
    const amount = Number(
      entry?.amount ??
      snapshot.overrideAmount ??
      snapshot.expectedCommissionAmount ??
      recipient.commissionAmount ??
      entry?.eventAmount ??
      0
    );
    const type = snapshot.type || (
      recipient.assignmentMode === "clientOnly"
        ? "clientRep"
        : recipient.assignmentMode === "clientOrder"
          ? "clientOrderRep"
          : "orderRep"
    );
    const note = entry?.note || snapshot.overrideNotes || snapshot.reason || "";

    setCreateForm({
      commissionableSubtotal: Number.isFinite(commissionableSubtotal) ? commissionableSubtotal.toFixed(2) : "0.00",
      marginAmount: Number.isFinite(marginAmount) ? marginAmount.toFixed(2) : "0.00",
      repUserId: entry?.repUserId ? String(entry.repUserId) : (recipient.repUserId ? String(recipient.repUserId) : ""),
      type,
      source: sourceBasis,
      ratePercent: Number.isFinite(rate) ? (rate * 100).toFixed(2) : "",
      amount: Number.isFinite(amount) ? amount.toFixed(2) : "0.00",
      note,
      paid: Boolean(entry?.paidAt || entry?.isPosted),
      relatedInvoiceId: "",
      entryType: "invoice_auto",
    });
    setAmountDirty(false);
    setCreateOpen(true);
    setEditingCommissionId(entry.id);
    setCommissionModalMode("edit");
  };

  useEffect(() => {
    const incoming = Array.isArray(assignedUsers) ? assignedUsers : [];
    const hasAnyUser = incoming.some((u) => u && (u.id != null || u.userId != null));
    if (hasAnyUser) {
      setStickyAssignedUsers(incoming);
    }
  }, [assignedUsers]);

  const commissionTypeOptions = [
    { id: "clientRep", label: "Client Rep" },
    { id: "orderRep", label: "Order Rep" },
    { id: "clientOrderRep", label: "Client & Order Rep" },
  ];

  const headers = { headers: { accessToken: localStorage.getItem("accessToken") } };

  const resolveRateFor = (repUserIdValue, typeValue) => {
    if (!repUserIdValue || !typeValue) return null;
    const assignment = commissionAssignments[String(repUserIdValue)];
    if (!assignment) return null;
    const rateId =
      typeValue === "clientRep"
        ? assignment.clientRepRateId
        : typeValue === "clientOrderRep"
        ? assignment.clientOrderRepRateId
        : assignment.orderRepRateId;
    if (!rateId) return null;
    return commissionRates.find((rate) => String(rate.id) === String(rateId)) || null;
  };

  const hasRateForType = (repUserIdValue, typeValue) => !!resolveRateFor(repUserIdValue, typeValue);
  const getMissingRateLabel = (typeLabel) => `${typeLabel} — no rate configured`;
  const getFirstAvailableTypeForRep = (repUserIdValue) =>
    commissionTypeOptions.find((option) => hasRateForType(repUserIdValue, option.id))?.id || "";

  const selectedTypeHasRate = hasRateForType(createForm.repUserId, createForm.type);
  const amountValueForSave = Number(createForm.amount || 0);
  const noteRequiredForSave = Number(createForm.amount || 0) < 0 || amountDirty;
  const isManualFlatAmount =
    (amountDirty || (commissionModalMode === "edit" && Number(createForm.ratePercent || 0) <= 0)) &&
    Math.abs(amountValueForSave) > 0.009 &&
    !!String(createForm.note || "").trim();
  const createDisabled =
    createBusy ||
    !createForm.repUserId ||
    !createForm.type ||
    (!selectedTypeHasRate && !isManualFlatAmount) ||
    !Number.isFinite(amountValueForSave) ||
    Math.abs(amountValueForSave) < 0.009 ||
    (noteRequiredForSave && !String(createForm.note || "").trim());

  //---------- actions ----------
  const doApprove = async () => {
    if (!orderId) return;
    try {
      setApproveBusy(true);
      await approveCommission(orderId, { basis: payoutBasis, rateOverride: Number(summary?.defaultRate ?? activeRate ?? 0), recipients: null, note: "" });
      const fresh = await getCommissionSummary(orderId);
      setSummary(fresh);
    } catch (e) {
      alert(e?.response?.data?.message || "Approve failed");
    } finally {
      setApproveBusy(false);
    }
  };


  const doRegenerate = async () => {
    if (!orderId) return;
    try {
      setApproveBusy(true);
      await regenerateCommission(orderId, {
        basis: payoutBasis,
        rateOverride: Number(summary?.defaultRate ?? activeRate ?? 0),
        note: "Regenerated from current invoice/bill actuals",
      });
      const [fresh, hist] = await Promise.all([getCommissionSummary(orderId), getCommissionHistory(orderId)]);
      setSummary(fresh);
      setHistory(hist);
    } catch (e) {
      alert(e?.response?.data?.message || "Regenerate failed");
    } finally {
      setApproveBusy(false);
    }
  };

  const doPay = async () => {
    if (!orderId) return;
    const amt = Number(payForm.amount || 0);
    if (!amt && payForm.kind === "payment") { alert("Enter an amount."); return; }
    try {
      setPayBusy(true);
      await payCommission(orderId, {
        amount: payForm.kind === "adjustment" ? Number(payForm.amount || 0) : amt,
        currency: "CAD",
        paidAt: payForm.paidAt || undefined,
        reference: payForm.reference || undefined,
        note: "",
        repUserId: payForm.repUserId || null,
        kind: payForm.kind,
      });
      const [fresh, hist] = await Promise.all([getCommissionSummary(orderId), getCommissionHistory(orderId)]);
      setSummary(fresh); setHistory(hist);
      setPayOpen(false); setPayForm({ amount: "", reference: "", paidAt: "", kind: "payment", repUserId: "" });
    } catch (e) {
      alert(e?.response?.data?.message || "Payment failed");
    } finally {
      setPayBusy(false);
    }
  };

  const openCreateCommission = () => {
    setCommissionModalMode("create");
    setEditingCommissionId(null);
    const firstRep = repOptions[0]?.id || "";
    const firstType = getFirstAvailableTypeForRep(firstRep);
    const defaultSource = payoutBasis === "margin" ? "margin" : "subtotal";
    const commissionableRevenueBase = manualCommissionSubtotal;
    setAmountDirty(false);
    setCreateForm({
      commissionableSubtotal: commissionableRevenueBase.toFixed(2),
      marginAmount: manualCommissionMarginAmount.toFixed(2),
      repUserId: firstRep,
      type: firstType,
      source: defaultSource,
      ratePercent: "",
      amount: "",
      note: "",
      paid: false,
      relatedInvoiceId: defaultRelatedInvoiceId,
      entryType: "manual",
    });
    setCreateOpen(true);
  };

  const createCommission = async () => {
    if (!orderId) return;
    const rateValue = Number(createForm.ratePercent || 0) / 100;
    const amountValue = Number(createForm.amount || 0);
    const payload = {
      repUserId: createForm.repUserId || null,
      type: createForm.type,
      source: createForm.source,
      entryType: "manual",
      amountWasManuallyEdited: !!amountDirty,
      commissionableSubtotal: Number(createForm.commissionableSubtotal || 0),
      marginAmount: Number(createForm.marginAmount || 0),
      rate: Number.isFinite(rateValue) ? rateValue : 0,
      amount: Number.isFinite(amountValue) ? amountValue : 0,
      note: createForm.note || "",
      paid: Boolean(createForm.paid),
      invoiceId: createForm.relatedInvoiceId || null,
      relatedInvoiceId: createForm.relatedInvoiceId || null,
    };
    try {
      setCreateBusy(true);
      if (commissionModalMode === "edit" && editingCommissionId) {
        await updateCommissionPayout(orderId, editingCommissionId, {
          ...payload,
          paid: Boolean(createForm.paid),
        });
      } else {
        await createManualCommission(orderId, payload);
      }
      if (orderId) {
        const [fresh, hist] = await Promise.all([getCommissionSummary(orderId), getCommissionHistory(orderId)]);
        setSummary(fresh);
        setHistory(hist);
      }
      setCreateOpen(false);
      setEditingCommissionId(null);
      setCommissionModalMode("create");
    } catch (e) {
      alert(e?.response?.data?.message || "Failed to create commission");
    } finally {
      setCreateBusy(false);
    }
  };

  useEffect(() => {
    if (!instanceUniqueId) return;
    const run = async () => {
      try {
        const instanceRes = await axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/byinstanceUniqueId/${instanceUniqueId}`,
          headers
        );
        const id = instanceRes.data?.id;
        if (!id) return;
        const effectiveAt = encodeURIComponent(summary?.commissionSettingsEffectiveAt || summary?.orderCreatedAt || "");
        const query = effectiveAt ? `?effectiveAt=${effectiveAt}` : "";
        const [ratesRes, assignmentsRes, marginTableRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/${id}/commission-rates${query}`, headers),
          axios.get(`${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/${id}/commission-assignments${query}`, headers),
          axios.get(`${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/${id}/commission-margin-table${query}`, headers),
        ]);
        let assignmentsRows = assignmentsRes.data?.assignments || [];
        // Fallback: if effective-dated lookup returns none, retry without effectiveAt
        // so assigned reps don't disappear in the UI.
        if ((!assignmentsRows || assignmentsRows.length === 0) && query) {
          try {
            const fallbackAssignmentsRes = await axios.get(
              `${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/${id}/commission-assignments`,
              headers
            );
            assignmentsRows = fallbackAssignmentsRes.data?.assignments || [];
          } catch (fallbackErr) {
            console.warn("Commission assignment fallback fetch failed", fallbackErr);
          }
        }
        setCommissionRates(ratesRes.data?.rates || []);
        const map = {};
        (assignmentsRows || []).forEach((row) => {
          map[String(row.userId)] = {
            clientRepRateId: row.clientRepRateId ?? "",
            orderRepRateId: row.orderRepRateId ?? "",
            clientOrderRepRateId: row.clientOrderRepRateId ?? "",
          };
        });
        if (Object.keys(map).length > 0) {
          setCommissionAssignments(map);
        } else {
          // Keep prior populated assignments instead of clobbering to empty (prevents rep row flicker/disappear).
          setCommissionAssignments((prev) => (Object.keys(prev || {}).length ? prev : {}));
        }
        setCommissionMarginTable(marginTableRes.data?.rows || []);
      } catch (e) {
        console.error("Failed to load commission settings", e);
      }
    };
    run();
  }, [instanceUniqueId, summary?.commissionSettingsEffectiveAt, summary?.orderCreatedAt]);

  const stampedTotalCost = Math.max(0, Number(summary?.targetBooked ?? targetBooked) || 0);
  const stampedRevenueSubtotalReference = Math.max(
    0,
    Number(
      summary?.bookedRevenueSubtotal ??
      summary?.revenueSubtotal ??
      stamped?.targetRevenue ??
      completedSO?.cart?.bookedRevenue ??
      summary?.revenue ??
      revenue
    ) || 0
  );
  const stampedRevenueInclTaxReference = Math.max(
    0,
    Number(
      summary?.revenueInclTax ??
      stamped?.targetRevenueInclTax ??
      completedSO?.cart?.orderSummary?.totalInclTax ??
      completedSO?.cart?.totalInclTax ??
      completedSO?.Total ??
      stampedRevenueSubtotalReference
    ) || 0
  );
  const stampedMarginReference = Math.max(
    0,
    Number(stamped?.bookedMargin ?? completedSO?.cart?.bookedMargin ?? summary?.bookedMargin ?? bookedMargin) || 0
  );
  const billedActualToDate = Math.max(
    0,
    Number(summary?.billedActualFromBills ?? 0),
    Number(summary?.billedActual ?? 0),
    Number(_billedActual ?? 0),
    Number(billedNum ?? 0),
  );
  const invoicedActualToDate = Math.max(
    0,
    Number(summary?.invoicedActualFromPaidInvoices ?? 0),
    Number(summary?.invoicedActual ?? 0),
    Number(_invoicedTotal ?? 0),
    Number(invoicedNum ?? 0),
  );
  const billedCostNoTaxFromSummary = Number(summary?.billedCostNoTaxActual);
  const billedCostNoTaxToDate = Number.isFinite(billedCostNoTaxFromSummary) && billedCostNoTaxFromSummary > 0
    ? billedCostNoTaxFromSummary
    : Math.max(0, billedActualToDate);
  const progressNumerator = useSalesSnapshot ? invoicedNum : billedCostNoTaxToDate;
  const progressDenominator = useSalesSnapshot ? stampedRevenueInclTaxReference : stampedTotalCost;
  const progressPct = progressDenominator > 0 ? (progressNumerator / progressDenominator) * 100 : 0;
  const progressLabel = useSalesSnapshot ? "Sales Snapshot" : "Actual";
  const progressVerb = useSalesSnapshot ? "invoiced" : "billed";
  // Commission calculations are based on billed (actual) costs, not stamped target cost.
  const totalCost = billedCostNoTaxToDate;
  const resolvedCommissionRevenue = Number(summary?.revenueSubtotal);
  const actualRevenueNoTaxToDate = Number.isFinite(resolvedCommissionRevenue)
    ? Math.max(0, resolvedCommissionRevenue)
    : Math.max(0, Number(summary?.actualRevenueNoTax ?? 0), stampedRevenueSubtotalReference);
  const totalRevenue = actualRevenueNoTaxToDate;
  const totalProfit = Math.max(0, totalRevenue - totalCost);
  const marginPercent = totalRevenue > 0 ? ((totalProfit / totalRevenue) * 100) : 0;
  const manualCommissionSubtotal = Math.max(
    0,
    Number(summary?.revenueSubtotal ?? 0) ||
    Number(summary?.invoiceSubtotal ?? 0) ||
    Number(summary?.orderRevenueSubtotal ?? 0) ||
    Number(summary?.commissionableSubtotal ?? 0) ||
    Number(stampedRevenueSubtotalReference ?? 0) ||
    0
  );
  const manualCommissionBilledCostNoTax = Math.max(
    0,
    Number(summary?.billedCostNoTax ?? 0) ||
    Number(summary?.actualBilledCostsExTax ?? 0) ||
    Number(summary?.billedCostActualNoTax ?? 0) ||
    Number(summary?.billedCostNoTaxActual ?? 0) ||
    Number(billedCostNoTaxToDate ?? 0) ||
    0
  );
  const manualCommissionMarginAmount = Math.max(
    0,
    manualCommissionSubtotal - manualCommissionBilledCostNoTax
  );
  const subtotalAllInvoices = Math.max(
    0,
    Number(
      summary?.totalRevenue ??
      summary?.invoiceSubtotal ??
      summary?.orderRevenueSubtotal ??
      summary?.commissionableSubtotal ??
      totalRevenue ??
      0
    ) || 0
  );
  const billedCostNoTax = Math.max(
    0,
    Number(
      summary?.billedCostNoTax ??
      summary?.actualBilledCostsExTax ??
      summary?.billedCostActualNoTax ??
      summary?.billedCostNoTaxActual ??
      billedCostNoTaxToDate ??
      0
    ) || 0
  );
  const billedMarginAmount = Math.max(0, subtotalAllInvoices - billedCostNoTax);
  const billedMarginPct = subtotalAllInvoices > 0
    ? (billedMarginAmount / subtotalAllInvoices) * 100
    : null;
  const bookedMarginAmount = Number(
    summary?.bookedMargin ??
    summary?.orderBookedMargin ??
    stamped?.bookedMargin ??
    summary?.stampedBookedAmount?.bookedMargin ??
    bookedMargin ??
    0
  ) || 0;
  const bookedMarginPctBasis = Number(summary?.bookedRevenueSubtotal || summary?.revenueSubtotal || stampedRevenueSubtotalReference || subtotalAllInvoices || 0);
  const bookedMarginPct = bookedMarginPctBasis > 0
    ? (bookedMarginAmount / bookedMarginPctBasis) * 100
    : null;
  const buildActualVarianceMeta = (actual, target) => {
    const diff = Number(actual || 0) - Number(target || 0);
    if (Math.abs(diff) < 0.005) return { text: "(on target)", color: "success.main" };
    if (diff > 0) return { text: `(${currency(diff)} over intended)`, color: "error.main" };
    return { text: `(${currency(Math.abs(diff))} under intended)`, color: "warning.main" };
  };
  const billedVarianceMeta = buildActualVarianceMeta(billedCostNoTaxToDate, stampedTotalCost);
  const invoicedVarianceMeta = buildActualVarianceMeta(invoicedActualToDate, stampedRevenueInclTaxReference);
  const outstandingInvoiceAmount = Math.max(0, stampedRevenueInclTaxReference - invoicedActualToDate);
  const outstandingBillAmount = Math.max(0, stampedTotalCost - billedCostNoTaxToDate);
  const waitingParts = [];
  if (outstandingInvoiceAmount > 0.005) waitingParts.push(`Outstanding invoice amount: ${currency(outstandingInvoiceAmount)}`);
  if (outstandingBillAmount > 0.005) waitingParts.push(`Outstanding bill amount: ${currency(outstandingBillAmount)}`);
  const waitingForActualsText = waitingParts.length ? `Waiting for: ${waitingParts.join(" / ")}` : null;
  const marginRuleMeta = useMemo(() => {
    const rows = Array.isArray(commissionMarginTable) ? commissionMarginTable : [];
    const hit = rows.find((row) => {
      const min = Number(row?.minMargin);
      const max = row?.maxMargin == null || row?.maxMargin === "" ? null : Number(row?.maxMargin);
      const lowerOk = Number.isFinite(min) ? marginPercent >= min : true;
      const upperOk = max == null || !Number.isFinite(max) ? true : marginPercent <= max;
      return lowerOk && upperOk;
    });
    const m = Number(hit?.multiplier);
    const normalizedMultiplier = Number.isFinite(m) && m > 0
      ? (m > 1 ? m / 100 : m)
      : 1;
    return {
      multiplier: normalizedMultiplier,
      rawMultiplier: Number.isFinite(m) ? m : null,
      min: Number(hit?.minMargin),
      max: hit?.maxMargin == null || hit?.maxMargin === "" ? null : Number(hit?.maxMargin),
    };
  }, [commissionMarginTable, marginPercent]);
  const marginMultiplier = marginRuleMeta.multiplier;

  const resolveRateIdForRep = (repUser, assignment) => {
    if (!assignment) return null;
    const isOrder = hasRole(repUser, "order-rep") || hasRole(repUser, "orderrep");
    const isClient = hasRole(repUser, "client-rep") || hasRole(repUser, "clientrep");
    const isAdmin = hasRole(repUser, "administrator") || hasRole(repUser, "admin");
    if (isOrder && isClient && assignment.clientOrderRepRateId) return assignment.clientOrderRepRateId;
    if (isAdmin && assignment.orderRepRateId) return assignment.orderRepRateId;
    if (isAdmin && assignment.clientOrderRepRateId) return assignment.clientOrderRepRateId;
    if (isOrder && assignment.orderRepRateId) return assignment.orderRepRateId;
    if (isClient && assignment.clientRepRateId) return assignment.clientRepRateId;
    return null;
  };

  const resolveRateIdForMode = (repUser, assignment, mode) => {
    if (!assignment) return null;
    if (mode === ASSIGNMENT_MODES.orderOnly) return assignment.orderRepRateId || null;
    if (mode === ASSIGNMENT_MODES.clientOnly) return assignment.clientRepRateId || null;
    if (mode === ASSIGNMENT_MODES.clientOrder) return assignment.clientOrderRepRateId || null;
    return assignment.clientOrderRepRateId || assignment.orderRepRateId || assignment.clientRepRateId || null;
  };

  const isEligibleForAssignmentMode = (repUser, assignment, mode) => {
    const isOrder = hasRole(repUser, "order-rep") || hasRole(repUser, "orderrep");
    const isClient = hasRole(repUser, "client-rep") || hasRole(repUser, "clientrep");
    const isAdmin = hasRole(repUser, "administrator") || hasRole(repUser, "admin");
    const hasClientRate = Boolean(assignment?.clientRepRateId);
    const hasOrderRate = Boolean(assignment?.orderRepRateId);
    const hasClientOrderRate = Boolean(assignment?.clientOrderRepRateId);
    if (mode === ASSIGNMENT_MODES.clientOnly) return isClient || (isAdmin && (hasClientRate || hasClientOrderRate));
    if (mode === ASSIGNMENT_MODES.orderOnly) return isOrder || (isAdmin && (hasOrderRate || hasClientOrderRate));
    if (mode === ASSIGNMENT_MODES.allAssigned) return isClient || isOrder || isAdmin;
    return isClient || isOrder || isAdmin || (isAdmin && (hasClientRate || hasOrderRate || hasClientOrderRate));
  };

  const autoCommissionRows = useMemo(() => {
    const baseUsers = (Array.isArray(assignedUsers) && assignedUsers.length)
      ? assignedUsers
      : stickyAssignedUsers;
    const reps = (Array.isArray(baseUsers) ? baseUsers : []).map((u) => mergeFromDirectory(u, userDirectory));
    const uniqueById = new Map();
    reps.forEach((r) => { if (r?.id != null) uniqueById.set(String(r.id), r); });
    const rows = [];
    uniqueById.forEach((repUser) => {
      const id = String(repUser?.id || "");
      const assignment = commissionAssignments[id];
      const isOrder = hasRole(repUser, "order-rep") || hasRole(repUser, "orderrep");
      const isClient = hasRole(repUser, "client-rep") || hasRole(repUser, "clientrep");
      const isAdmin = hasRole(repUser, "administrator") || hasRole(repUser, "admin");
      const isSupportedRole = isOrder || isClient || isAdmin;
      if (!isSupportedRole) return;
      const rateId = resolveRateIdForMode(repUser, assignment, assignmentMode);
      let roleLabel = "Rep";
      if (isOrder && isClient && rateId) {
        roleLabel = "Client + Order Rep";
      } else if (isAdmin && rateId) {
        // Administrators can also be commission recipients; use assigned base rate mapping.
        roleLabel = "Administrator";
      } else if (isOrder && rateId) {
        roleLabel = "Order Rep";
      } else if (isClient && rateId) {
        roleLabel = "Client Rep";
      }
      const rateRow = commissionRates.find((r) => String(r.id) === String(rateId));
      const repRate = Number(rateRow?.rate || 0);
      const rateBasis = String(rateRow?.rateType || "margin").toLowerCase() === "sales" ? "subtotal" : "margin";
      const commissionBaseAmount = rateBasis === "subtotal"
        ? manualCommissionSubtotal
        : manualCommissionMarginAmount;
      const baseCommission = commissionBaseAmount;
      const eligibleForMode = Boolean(rateId) && isEligibleForAssignmentMode(repUser, assignment, assignmentMode);
      const finalCommission = eligibleForMode
        ? (rateBasis === "subtotal" ? baseCommission * repRate : baseCommission * repRate * marginMultiplier)
        : 0;
      const marginBandLabel = Number.isFinite(marginRuleMeta.min)
        ? `${marginRuleMeta.min}%${marginRuleMeta.max == null || !Number.isFinite(marginRuleMeta.max) ? '+' : `–${marginRuleMeta.max}%`}`
        : "No margin table match";
      rows.push({
        repUserId: id,
        selectedRateId: rateId ? String(rateId) : "",
        repName: getName(repUser) || repUser?.email || `Rep #${id}`,
        roleLabel,
        repRate,
        baseCommission,
        finalCommission,
        commissionBaseAmount,
        rateBasis,
        eligibleForMode,
        rateName: rateRow?.name || `Rate #${rateId}`,
        rateType: String(rateRow?.rateType || "margin"),
        marginBandLabel,
        marginMultiplierUsed: marginMultiplier,
      });
    });
    return rows;
  }, [assignedUsers, stickyAssignedUsers, userDirectory, commissionAssignments, commissionRates, manualCommissionSubtotal, manualCommissionMarginAmount, marginMultiplier, marginRuleMeta.min, marginRuleMeta.max, assignmentMode]);

  const excludedRepNames = useMemo(() => {
    const baseUsers = (Array.isArray(assignedUsers) && assignedUsers.length)
      ? assignedUsers
      : stickyAssignedUsers;
    const reps = (Array.isArray(baseUsers) ? baseUsers : []).map((u) => mergeFromDirectory(u, userDirectory));
    const uniqueById = new Map();
    reps.forEach((r) => { if (r?.id != null) uniqueById.set(String(r.id), r); });
    const out = [];
    uniqueById.forEach((repUser) => {
      const id = String(repUser?.id || "");
      const isCommissionRole =
        hasRole(repUser, "order-rep") ||
        hasRole(repUser, "orderrep") ||
        hasRole(repUser, "client-rep") ||
        hasRole(repUser, "clientrep") ||
        hasRole(repUser, "administrator") ||
        hasRole(repUser, "admin");
      if (!isCommissionRole) return;
      const assignment = commissionAssignments[id];
      const rateId = resolveRateIdForRep(repUser, assignment);
      if (!assignment || !rateId) out.push(getName(repUser) || repUser?.email || `Rep #${id}`);
    });
    return out;
  }, [assignedUsers, stickyAssignedUsers, userDirectory, commissionAssignments]);

  const eligibleRepCount = useMemo(
    () => autoCommissionRows.filter((row) => row.eligibleForMode).length,
    [autoCommissionRows]
  );
  const assignmentModeLabel = ASSIGNMENT_MODE_OPTIONS.find((opt) => opt.value === assignmentMode)?.label || "Client + Order Rep";

  useEffect(() => {
    if (!createOpen) return;
    if (!repOptions.length && !createForm.repUserId) return;
    const rate = resolveRateFor(createForm.repUserId, createForm.type);
    if (!rate) {
      setCreateForm((prev) => ({
        ...prev,
        ratePercent: "",
        amount: amountDirty ? prev.amount : "",
      }));
      return;
    }
    const ratePercent = Number(rate.rate || 0) * 100;
    setCreateForm((prev) => ({
      ...prev,
      ratePercent: Number.isFinite(ratePercent) ? ratePercent.toFixed(2) : "",
      source: rate.rateType === "sales" ? "subtotal" : "margin",
    }));
  }, [createOpen, createForm.repUserId, createForm.type, repOptions.length, commissionRates, commissionAssignments, amountDirty]);

  useEffect(() => {
    if (!createOpen) return;
    if (amountDirty) return;
    const rateValue = Number(createForm.ratePercent || 0) / 100;
    const marginAmount = Number(createForm.marginAmount || 0);
    const subtotal = Number(createForm.commissionableSubtotal || 0);
    const multiplier = Number(marginMultiplier || 1);
    if (!Number.isFinite(rateValue) || !Number.isFinite(marginAmount) || !Number.isFinite(subtotal)) return;

    let calculatedAmount = 0;
    if (createForm.source === "margin") {
      calculatedAmount = marginAmount * rateValue * (Number.isFinite(multiplier) ? multiplier : 1);
    } else if (createForm.source === "subtotal") {
      calculatedAmount = subtotal * rateValue;
    }

    const nextAmount = Number.isFinite(calculatedAmount) ? calculatedAmount.toFixed(2) : "0.00";
    setCreateForm((prev) => (prev.amount === nextAmount ? prev : { ...prev, amount: nextAmount }));
  }, [createOpen, createForm.ratePercent, createForm.source, createForm.marginAmount, createForm.commissionableSubtotal, marginMultiplier, amountDirty]);

  const commissionDue = summary?.commissionDue ?? ((Math.max(0, invoicedNum - billedNum)) * activeRate);
  const paid = summary?.paid ?? 0;
  const balance = summary?.balance ?? Math.max(0, commissionDue - paid);
  const state = summary?.state || "DRAFT";
  const totalCommission = Math.max(0, Number(commissionDue) || 0);

  // Mark Paid constraints
  const maxBalance = Number((balance || 0).toFixed(2));
  const amtNum = Number(payForm.amount || 0);
  const amtTooHigh = payForm.kind === "payment" && amtNum > maxBalance + 1e-8;
  const saveDisabled = payBusy || !summary?.lock || (payForm.kind === "payment" && amtTooHigh);




const basedOnLabel = (value) =>
  String(value || "").toLowerCase() === "subtotal" ? "Revenue" : "Profit";

const recipientReadOnly = !canFinance;

const computeRecipientAmount = (row) => {
  const rate = Math.max(0, Number(row.baseRatePercent || 0) / 100);
  const base =
    String(row.basedOn || "").toLowerCase() === "subtotal"
      ? manualCommissionSubtotal
      : manualCommissionMarginAmount;

  return base * rate;
};

useEffect(() => {
  const rows = Array.isArray(summary?.recipientBreakdown)
    ? summary.recipientBreakdown.map((row) => ({
        repUserId: row.repUserId ? String(row.repUserId) : "",
        repName: row.repName || "",
        roleType: row.roleType || "manual",
        basedOn: row.basedOn || "margin",
        baseRatePercent: (Number(row.baseRate || 0) * 100).toFixed(2),
        amount: Number(row.commissionDue || 0),
        provenance: row.provenance || "system",
      }))
    : [];

  if (rows.length > 0) {
    setRecipientEdits(rows);
    return;
  }

  if (autoCommissionRows.length > 0) {
    setRecipientEdits(
      autoCommissionRows.map((row) => ({
        repUserId: row.repUserId ? String(row.repUserId) : "",
        repName: row.repName || "",
        roleType: row.roleLabel || "system",
        basedOn: row.rateBasis === "subtotal" ? "subtotal" : "margin",
        baseRatePercent: (Number(row.repRate || 0) * 100).toFixed(2),
        amount: Number(row.finalCommission || 0),
        provenance: "system",
      }))
    );
    return;
  }

  setRecipientEdits([]);
}, [summary?.recipientBreakdown, autoCommissionRows]);

const addRecipientRow = () => {
  const fallbackId = repOptions[0]?.id ? String(repOptions[0].id) : "";
  const fallbackRep = repOptions.find((r) => String(r.id) === fallbackId);

  setRecipientEdits((prev) => [
    ...prev,
    {
      repUserId: fallbackId,
      repName: fallbackRep?.name || "",
      roleType: "manual",
      basedOn: payoutBasis === "margin" ? "margin" : "subtotal",
      baseRatePercent: "0.00",
      amount: 0,
      provenance: "manual",
    },
  ]);
};




const computedCommissionDue = autoCommissionRows
  .filter((r) => r.eligibleForMode)
  .reduce((sum, r) => sum + (Number(r.finalCommission) || 0), 0);

const commissionDueDisplay =
  Math.abs(computedCommissionDue) > 0.009
    ? computedCommissionDue
    : Number(commissionDue || 0);

const payoutToDateDisplay = commissionDueDisplay;
const balanceDisplay = Math.max(0, commissionDueDisplay - paid);






const saveRecipientOverrides = async () => {
  if (!orderId) return;

  try {
    setSavingRecipients(true);

    const recipients = recipientEdits
      .map((row) => {
        const baseRate = Number(row.baseRatePercent || 0) / 100;

        if (!Number.isFinite(baseRate) || baseRate < 0) {
          return null;
        }

        return {
          repUserId: row.repUserId ? Number(row.repUserId) : null,
          roleType: row.roleType || "manual",
          baseRate,
          basedOn: row.basedOn || "margin",
          rateName: row.provenance === "manual" ? "Manual Override" : "System Rate",
        };
      })
      .filter(Boolean);

    await approveCommission(orderId, {
      basis: payoutBasis,
      rateOverride: Number(summary?.defaultRate ?? activeRate ?? 0),
      recipients,
      note: "Updated commission recipients/overrides",
    });

    const [fresh, hist] = await Promise.all([
      getCommissionSummary(orderId),
      getCommissionHistory(orderId),
    ]);

    setSummary(fresh);
    setHistory(hist);
  } catch (e) {
    alert(e?.response?.data?.message || "Failed to save recipient overrides");
  } finally {
    setSavingRecipients(false);
  }
};
  /* ---------------- UI ---------------- */
  const stateColor = state === "PAID" ? "success" : state === "APPROVED" ? "primary" : "default";

  if (loading) {
    return (
      <Paper sx={{ p: 4, display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          Loading commission data…
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 0, overflow: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2.5,
          py: 1.5,
          borderBottom: "3px solid",
          borderBottomColor: "primary.main",
          bgcolor: "background.paper",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <MonetizationOnIcon sx={{ color: "primary.main", fontSize: 22 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1rem" }}>
            Commissions
          </Typography>
          <Chip
            size="small"
            label={state}
            color={stateColor}
            sx={{ fontWeight: 600, fontSize: "0.7rem" }}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          {/*<Tooltip title="Recalculate from latest actuals." arrow>
            <span>
              <IconButton
                size="small"
                onClick={doRegenerate}
                disabled={!orderId || approveBusy}
                sx={{ color: "text.secondary" }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>*/}

          <Tooltip title="Add a manual commission entry." arrow>
            <span>
              <IconButton
                size="small"
                onClick={openCreateCommission}
                disabled={!orderId}
                sx={{ color: "text.secondary" }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 24, alignSelf: "center" }} />

          {/*canFinance && orderId && (
            <>
              {!summary?.lock ? (
                <Tooltip arrow title={summary?.canApprove ? "" : (summary?.approveReasons || ["Not ready"]).join(" • ")}>
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={<CheckCircleOutlineIcon />}
                      onClick={doApprove}
                      disabled={approveBusy || !summary?.canApprove}
                      sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                      {approveBusy ? "Approving…" : "Approve"}
                    </Button>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip title="Re-approve at a different rate/snapshot.">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={doApprove}
                      disabled={approveBusy}
                      sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                      {approveBusy ? "Re-approving…" : "Re-Approve"}
                    </Button>
                  </span>
                </Tooltip>
              )}

              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<PaymentsIcon />}
                onClick={() => setPayOpen(true)}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Mark Paid
              </Button>
            </>
          )*/}
        </Box>
      </Box>

      <Box sx={{ p: 2.5 }}>
        {/*<Grid container spacing={1.5} sx={{ mb: 2.5 }}>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard label="Total Revenue" value={currency(totalRevenue)} icon={TrendingUpIcon} iconColor="primary.main" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard label="Billed Cost (Actual)" value={currency(totalCost)} icon={AccountBalanceWalletIcon} iconColor="warning.main" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard label="Total Profit (Margin)" value={currency(totalProfit)} icon={PriceCheckIcon} iconColor="success.main" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard label="Commission Due" value={currency(commissionDueDisplay)} icon={PercentIcon} iconColor="error.main" highlight />
          </Grid>
        </Grid>*/}

        {/*<SectionCard
          title="Payout Summary"
          icon={AccountBalanceWalletIcon}
          action={
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  color="primary"
                  checked={useSalesSnapshot}
                  onChange={(e) => setUseSalesSnapshot(e.target.checked)}
                />
              }
              label={
                <Tooltip title={useSalesSnapshot ? "Invoiced / target revenue snapshot." : "Billed cost / target cost snapshot."} arrow>
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    {useSalesSnapshot ? "Sales Snapshot" : "Actual"}
                  </Typography>
                </Tooltip>
              }
              sx={{ m: 0 }}
            />
          }
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Stack spacing={0.25}>
                <InfoRow label="Actual Margin" value={currency(totalProfit)} />
                <InfoRow label="Commission Due" value={currency(commissionDueDisplay)} />
                <InfoRow label="Paid to Date" value={currency(paid)} />
                <InfoRow
                  label="Balance Remaining"
                  value={currency(balanceDisplay)}
                  badge={
                    balanceDisplay > 0
                      ? <Chip size="small" label="Unpaid" color="warning" sx={{ height: 18, fontSize: "0.65rem" }} />
                      : <Chip size="small" label="Settled" color="success" sx={{ height: 18, fontSize: "0.65rem" }} />
                  }
                  last
                />
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              {progressDenominator > 0 && (
                <Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">{progressLabel} Progress</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, fontFamily: "monospace" }}>{progressPct.toFixed(1)}%</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={Math.min(100, progressPct)} sx={{ height: 8, borderRadius: 2, mb: 0.75 }} />
                  <Typography variant="caption" color="text.secondary">
                    {currency(progressNumerator)} {progressVerb} of {currency(progressDenominator)}
                  </Typography>
                </Box>
              )}
              {summary?.lock && (
                <Alert severity="success" sx={{ mt: 1.5, py: 0.5 }}>
                  Locked at {(Number(summary.lock.rate) * 100).toFixed(1)}% on {new Date(summary.lock.lockedAt).toLocaleString()}
                </Alert>
              )}
            </Grid>
          </Grid>
        </SectionCard>*/}

        {/*<SectionCard title="Financial Breakdown" icon={InfoOutlinedIcon}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, mb: 0.75, display: "block" }}>
                Stamp References
              </Typography>
              <Stack spacing={0.25}>
                <InfoRow label="Subtotal (Order Total)" value={currency(stampedRevenueSubtotalReference)} />
                <InfoRow label="Total incl. Tax" value={currency(stampedRevenueInclTaxReference)} />
                <InfoRow label="Total Cost" value={currency(stampedTotalCost)} />
                <InfoRow label="Margin (Stamp Ref)" value={currency(stampedMarginReference)} last />
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, mb: 0.75, display: "block" }}>
                Actuals to Date
              </Typography>
              <Stack spacing={0.25}>
                <InfoRow
                  label="Billed (Actual)"
                  value={currency(billedActualToDate)}
                  badge={<Typography variant="caption" sx={{ color: billedVarianceMeta.color, fontWeight: 600 }}>{billedVarianceMeta.text}</Typography>}
                  tooltip="Sum of supplier bills entered so far."
                />
                <InfoRow
                  label="Billed Cost (No Tax)"
                  value={currency(billedCostNoTaxToDate)}
                  tooltip="Billed subtotal excluding taxes; used for actual margin and margin-based commissions."
                />
                <InfoRow
                  label="Invoiced (Client, Paid)"
                  value={currency(invoicedActualToDate)}
                  badge={<Typography variant="caption" sx={{ color: invoicedVarianceMeta.color, fontWeight: 600 }}>{invoicedVarianceMeta.text}</Typography>}
                />
                <InfoRow
                  label="Margin % (Actual)"
                  value={`${marginPercent.toFixed(2)}%`}
                  badge={
                    <Chip size="small" label={`×${marginMultiplier.toFixed(2)} multiplier`} variant="outlined" sx={{ height: 18, fontSize: "0.65rem" }} />
                  }
                  last
                />
              </Stack>
              {waitingForActualsText && (
                <Alert severity="warning" sx={{ mt: 1, py: 0.5, fontSize: "0.75rem" }}>{waitingForActualsText}</Alert>
              )}
            </Grid>
          </Grid>
        </SectionCard>*/}

        {!!history.length && (
          <SectionCard
            title="Commission History"
            icon={HistoryIcon}
            action={
              <ToggleButtonGroup
                size="small"
                value={historyView}
                exclusive
                onChange={(_, next) => next && setHistoryView(next)}
              >
                <ToggleButton value="records">Records</ToggleButton>
                <ToggleButton value="history">History</ToggleButton>
              </ToggleButtonGroup>
            }
          >
            <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mb: 1 }}>
              Double-click an unpaid manual or invoice auto row to edit it.
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700, fontSize: "0.75rem", color: "text.secondary", bgcolor: "action.hover", py: 1, borderBottom: "2px solid", borderBottomColor: "divider" } }}>
                  {historyView === "history" && <TableCell>Date</TableCell>}
                  <TableCell>Rep</TableCell>
                  <TableCell>{historyView === "history" ? "Event" : "Type"}</TableCell>
                  <TableCell>Rate</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>{historyView === "history" ? "Status" : "Paid"}</TableCell>
                  {historyView === "history" && <TableCell>Reference</TableCell>}
                  <TableCell>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleCommissionRows.map((h) => {
                  const rowType = String(h?.entryType || "").toLowerCase();
                  const rowPaid = isRecordRowPaid(h);
                  const rowPartiallyPaid = isRecordRowPartiallyPaid(h);
                  const rowHasPaymentActivity = rowPaid || rowPartiallyPaid;
                  const isEditable = historyView === "records"
                    && ["manual", "invoice_auto", "adjustment"].includes(rowType)
                    && !rowHasPaymentActivity
                    && !h?.paidAt
                    && !h?.isPosted;
                  const repLabel = h?.repUserId ? (repNameById.get(String(h.repUserId)) || `User #${h.repUserId}`) : "—";
                  const paidLabel = getRecordPaidLabel(h);
                  const historyStatus = getHistoryStatusLabel(h);
                  return (
                    <TableRow
                      key={`hist-${h.id}`}
                      hover={isEditable}
                      onDoubleClick={() => isEditable && openEditCommission(h)}
                      title={!isEditable && historyView === "records" && rowHasPaymentActivity ? "Unpay this commission before editing." : undefined}
                      sx={isEditable ? { cursor: "pointer" } : { opacity: 0.8 }}
                    >
                      {historyView === "history" && <TableCell>{h?.eventDate ? new Date(h.eventDate).toLocaleDateString() : "—"}</TableCell>}
                      <TableCell>{repLabel}</TableCell>
                      <TableCell>{String(h?.entryType || "—").toUpperCase()}</TableCell>
                      <TableCell>{Number(h?.rate) > 0 ? `${(Number(h.rate) * 100).toFixed(2)}%` : "—"}</TableCell>
                      <TableCell>{currency(h?.amount)}</TableCell>
                      <TableCell>{historyView === "history" ? historyStatus : paidLabel}</TableCell>
                      {historyView === "history" && <TableCell>{h?.reference || "—"}</TableCell>}
                      <TableCell>{h?.note || h?.reference || "—"}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </SectionCard>
        )}

        <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              label="Subtotal (all invoices)"
              value={currency(subtotalAllInvoices)}
              icon={TrendingUpIcon}
              iconColor="primary.main"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              label="Billed Margin Amount"
              value={currency(billedMarginAmount)}
              icon={AccountBalanceWalletIcon}
              iconColor="warning.main"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              label="Billed Margin"
              value={billedMarginPct == null ? "—" : `${billedMarginPct.toFixed(2)}%`}
              icon={PriceCheckIcon}
              iconColor="success.main"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              label="Booked Margin"
              value={`${currency(bookedMarginAmount)} / ${
                bookedMarginPct == null ? "0.00%" : `${bookedMarginPct.toFixed(2)}%`
              }`}
              icon={PercentIcon}
              iconColor="error.main"
              highlight
            />
          </Grid>
        </Grid>

      {/*<SectionCard
        title="Commission Recipients"
        icon={PersonIcon}
        action={
          canFinance && (
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addRecipientRow}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Add Rep
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={saveRecipientOverrides}
                disabled={savingRecipients || recipientReadOnly}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                {savingRecipients ? "Saving…" : "Save Overrides"}
              </Button>
            </Stack>
          )
        }
      >
        {recipientEdits.length ? (
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    bgcolor: "action.hover",
                    py: 1,
                    borderBottom: "2px solid",
                    borderBottomColor: "divider",
                  },
                }}
              >
                <TableCell>Rep</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Based On</TableCell>
                <TableCell align="right">Rate %</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell width={48}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recipientEdits.map((row, idx) => {
                const amount = computeRecipientAmount(row);
                const selectedRep = repOptions.find(
                  (repOption) => String(repOption.id) === String(row.repUserId)
                );

                return (
                  <TableRow key={`recipient-${idx}`}>
                    <TableCell>
                      {recipientReadOnly ? (
                        <Typography variant="body2">
                          {row.repName || selectedRep?.name || "—"}
                        </Typography>
                      ) : (
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={row.repUserId || ""}
                          onChange={(e) => {
                            const repUserId = e.target.value;
                            const repOption = repOptions.find(
                              (option) => String(option.id) === String(repUserId)
                            );
                            setRecipientEdits((prev) =>
                              prev.map((item, itemIdx) =>
                                itemIdx === idx
                                  ? {
                                      ...item,
                                      repUserId,
                                      repName: repOption?.name || "",
                                    }
                                  : item
                              )
                            );
                          }}
                        >
                          {repOptions.map((repOption) => (
                            <MenuItem key={repOption.id} value={String(repOption.id)}>
                              {repOption.name} — {repOption.roles}
                            </MenuItem>
                          ))}
                          {!repOptions.length && (
                            <MenuItem value="" disabled>
                              No reps available
                            </MenuItem>
                          )}
                        </TextField>
                      )}
                    </TableCell>

                    <TableCell>
                      {recipientReadOnly ? (
                        <Chip
                          size="small"
                          label={row.roleType || "manual"}
                          variant="outlined"
                          sx={{ fontSize: "0.7rem", height: 20 }}
                        />
                      ) : (
                        <TextField
                          size="small"
                          fullWidth
                          value={row.roleType || ""}
                          onChange={(e) =>
                            setRecipientEdits((prev) =>
                              prev.map((item, itemIdx) =>
                                itemIdx === idx
                                  ? { ...item, roleType: e.target.value }
                                  : item
                              )
                            )
                          }
                        />
                      )}
                    </TableCell>

                    <TableCell>
                      {recipientReadOnly ? (
                        <Typography variant="body2">
                          {basedOnLabel(row.basedOn)}
                        </Typography>
                      ) : (
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={row.basedOn || "margin"}
                          onChange={(e) =>
                            setRecipientEdits((prev) =>
                              prev.map((item, itemIdx) =>
                                itemIdx === idx
                                  ? { ...item, basedOn: e.target.value }
                                  : item
                              )
                            )
                          }
                        >
                          <MenuItem value="margin">Profit</MenuItem>
                          <MenuItem value="subtotal">Revenue</MenuItem>
                        </TextField>
                      )}
                    </TableCell>

                    <TableCell align="right">
                      {recipientReadOnly ? (
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "monospace" }}
                        >
                          {Number(row.baseRatePercent || 0).toFixed(2)}%
                        </Typography>
                      ) : (
                        <TextField
                          size="small"
                          type="number"
                          value={row.baseRatePercent}
                          inputProps={{ min: 0, step: 0.01 }}
                          onChange={(e) =>
                            setRecipientEdits((prev) =>
                              prev.map((item, itemIdx) =>
                                itemIdx === idx
                                  ? { ...item, baseRatePercent: e.target.value }
                                  : item
                              )
                            )
                          }
                          sx={{ width: 110 }}
                        />
                      )}
                    </TableCell>

                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "monospace",
                          fontWeight: 700,
                          color: amount > 0 ? "success.main" : "text.secondary",
                        }}
                      >
                        {currency(amount)}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      {!recipientReadOnly && (
                        <Tooltip title="Remove recipient" arrow>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() =>
                                setRecipientEdits((prev) =>
                                  prev.filter((_, itemIdx) => itemIdx !== idx)
                                )
                              }
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : (
          <Box sx={{ py: 2.5, textAlign: "center" }}>
            <PersonIcon sx={{ fontSize: 32, color: "text.disabled", mb: 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              No commission recipients configured yet.
            </Typography>
          </Box>
        )}
      </SectionCard>*/}

      {/*<SectionCard title="Assigned Rep" icon={PersonIcon}>
        {rep ? (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar src={avatarUrl || undefined} sx={{ width: 44, height: 44 }}>
              {fullName?.charAt(0)?.toUpperCase() || "R"}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {fullName || "Assigned Rep"}
              </Typography>
              {email ? (
                <Link
                  href={`mailto:${email}`}
                  variant="caption"
                  sx={{ display: "block", wordBreak: "break-all" }}
                >
                  {email}
                </Link>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  No email available
                </Typography>
              )}
            </Box>
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No client rep assigned.
          </Typography>
        )}
      </SectionCard>*/}

      {canViewDebugInputs && (
        <Box sx={{ mt: 2 }}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">
                Pre-populated Commission Inputs (Debug)
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">
                Subtotal (Order Total, Stamp Reference):{" "}
                <strong>{currency(stampedRevenueSubtotalReference)}</strong>
              </Typography>
              <Typography variant="body2">
                Total (incl tax) (Order Total + taxes):{" "}
                <strong>{currency(stampedRevenueInclTaxReference)}</strong>
              </Typography>
              <Typography variant="body2">
                Total Cost (Stamp Reference):{" "}
                <strong>{currency(stampedTotalCost)}</strong>
              </Typography>
              <Typography variant="body2">
                Billed (Actual) to-date:{" "}
                <strong>{currency(billedActualToDate)}</strong>
              </Typography>
              <Typography variant="body2">
                Billed cost with no taxes (Actual):{" "}
                <strong>{currency(billedCostNoTaxToDate)}</strong>
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ ml: 0.75, color: billedVarianceMeta.color }}
                >
                  {billedVarianceMeta.text}
                </Typography>
              </Typography>
              <Typography variant="body2">
                Invoiced (Actual, Paid) to-date:{" "}
                <strong>{currency(invoicedActualToDate)}</strong>
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ ml: 0.75, color: invoicedVarianceMeta.color }}
                >
                  {invoicedVarianceMeta.text}
                </Typography>
              </Typography>
              <Typography variant="body2">
                Margin (Stamp Reference):{" "}
                <strong>{currency(stampedMarginReference)}</strong>
              </Typography>
              <Typography variant="body2">
                Margin (Actual): <strong>{currency(totalProfit)}</strong>
                {waitingForActualsText ? (
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ ml: 0.75, color: "warning.main" }}
                  >
                    ({waitingForActualsText})
                  </Typography>
                ) : null}
              </Typography>
              <Typography variant="body2">
                Margin % (Actual): <strong>{marginPercent.toFixed(2)}%</strong>
              </Typography>
              <Typography variant="body2">
                Margin Multiplier:{" "}
                <strong>{marginMultiplier.toFixed(2)}</strong>
                {marginRuleMeta.rawMultiplier != null &&
                Number(marginRuleMeta.rawMultiplier) > 1
                  ? ` (from table value ${marginRuleMeta.rawMultiplier})`
                  : ""}
              </Typography>

              <Box
                sx={{
                  mt: 1,
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  select
                  size="small"
                  label="Assignment Mode"
                  value={assignmentMode}
                  onChange={(e) => setAssignmentMode(e.target.value)}
                  sx={{ minWidth: 260 }}
                >
                  {ASSIGNMENT_MODE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Using {assignmentModeLabel} → <strong>{eligibleRepCount}</strong>{" "}
                  reps eligible
                </Typography>
              </Box>
            </AccordionDetails>

            <SectionCard
              title="Auto Commission Calculation"
              icon={PercentIcon}
              action={
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <TextField
                    select
                    size="small"
                    label="Assignment Mode"
                    value={assignmentMode}
                    onChange={(e) => setAssignmentMode(e.target.value)}
                    sx={{ minWidth: 240 }}
                  >
                    {ASSIGNMENT_MODE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </TextField>
                  <Typography variant="caption" color="text.secondary">
                    <strong>{eligibleRepCount}</strong> reps eligible
                  </Typography>
                </Stack>
              }
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ "& th": { fontWeight: 700, fontSize: "0.75rem", color: "text.secondary", bgcolor: "action.hover", py: 1, borderBottom: "2px solid", borderBottomColor: "divider" } }}>
                    <TableCell>Rep</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell align="right">Rate %</TableCell>
                    <TableCell align="right">Base Commission</TableCell>
                    <TableCell align="right">Final Commission</TableCell>
                    <TableCell>Applied Settings</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {autoCommissionRows.length ? (
                    autoCommissionRows.map((row, idx) => (
                      <TableRow
                        key={`${row.repName}-${idx}`}
                        sx={{
                          bgcolor: row.eligibleForMode ? "inherit" : "action.disabledBackground",
                          opacity: row.eligibleForMode ? 1 : 0.65,
                          "&:last-child td": { border: 0 },
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Avatar sx={{ width: 24, height: 24, fontSize: "0.7rem", bgcolor: "primary.main" }}>
                              {row.repName?.charAt(0)?.toUpperCase() || "R"}
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.repName}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={row.roleLabel} variant="outlined" sx={{ fontSize: "0.7rem", height: 20 }} />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                            {(row.repRate * 100).toFixed(2)}%
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontFamily: "monospace" }}>{currency(row.baseCommission)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 700, color: row.eligibleForMode ? "success.main" : "text.secondary" }}>
                            {currency(row.finalCommission)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ display: "block", lineHeight: 1.6 }}>Rate: <strong>{row.rateName}</strong> ({row.rateType})</Typography>
                          <Typography variant="caption" sx={{ display: "block", lineHeight: 1.6 }}>Assignment: <strong>{assignmentModeLabel}</strong></Typography>
                          <Typography variant="caption" sx={{ display: "block", lineHeight: 1.6 }}>Band: <strong>{row.marginBandLabel}</strong></Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.6 }}>
                            Formula: {currency(row.commissionBaseAmount)} × {(row.repRate * 100).toFixed(2)}% × {Number(row.marginMultiplierUsed || 1).toFixed(2)}
                          </Typography>
                          {!row.eligibleForMode && (
                            <Typography variant="caption" sx={{ display: "block", color: "warning.main" }}>
                              Not eligible for selected mode
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Box sx={{ py: 2.5, textAlign: "center" }}>
                          <PercentIcon sx={{ fontSize: 32, color: "text.disabled", mb: 0.5 }} />
                          <Typography variant="body2" color="text.secondary">No eligible assigned reps / rates found.</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <Typography variant="body2" sx={{ mt: 1 }}> 
              Total Commission Payout:{" "}
              <strong>
                {currency(
                  autoCommissionRows
                    .filter((r) => r.eligibleForMode)
                    .reduce((sum, r) => sum + (Number(r.finalCommission) || 0), 0)
                )}
              </strong>
            </Typography>
            {excludedRepNames.length > 0 && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Excluded from payout because assignment/rate is missing or changed after this project snapshot:{" "}
                <strong>{excludedRepNames.join(", ")}</strong>.
              </Alert>
            )}
          </SectionCard>

          </Accordion>
        </Box>
      )}

      {/* CREATE COMMISSION dialog */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {commissionModalMode === "edit" && editingCommissionId
            ? "Edit Commission"
            : "Add Commission"}
        </DialogTitle>
        <DialogContent sx={{ pt: 1, display: "grid", gap: 1.5 }}>
          <TextField
            label="Commissionable Subtotal"
            value={createForm.commissionableSubtotal}
            disabled
            fullWidth
            sx={disabledContextFieldSx}
          />
          <TextField
            label="Margin Amount"
            value={createForm.marginAmount}
            disabled
            fullWidth
            sx={disabledContextFieldSx}
          />
          <TextField
            label="Rep"
            select
            value={createForm.repUserId}
            onChange={(e) => {
              const nextRepUserId = e.target.value;
              const currentTypeStillValid = hasRateForType(
                nextRepUserId,
                createForm.type
              );
              const nextType = currentTypeStillValid
                ? createForm.type
                : getFirstAvailableTypeForRep(nextRepUserId);
              setCreateForm((prev) => ({
                ...prev,
                repUserId: nextRepUserId,
                type: nextType,
              }));
              setAmountDirty(false);
            }}
          >
            {repOptions.map((repOption) => (
              <MenuItem key={repOption.id} value={repOption.id}>
                {repOption.name} — {repOption.roles}
              </MenuItem>
            ))}
            {!repOptions.length && (
              <MenuItem value="" disabled>
                No reps available
              </MenuItem>
            )}
          </TextField>
          <TextField
            label="Type"
            select
            value={createForm.type}
            onChange={(e) => {
              const nextType = e.target.value;
              if (!hasRateForType(createForm.repUserId, nextType)) return;
              setCreateForm((prev) => ({ ...prev, type: nextType }));
              setAmountDirty(false);
            }}
            fullWidth
          >
            {!createForm.type && (
              <MenuItem value="" disabled>
                Select a configured commission type
              </MenuItem>
            )}
            {commissionTypeOptions.map((option) => {
              const disabled =
                !!createForm.repUserId &&
                !hasRateForType(createForm.repUserId, option.id);
              return (
                <MenuItem key={option.id} value={option.id} disabled={disabled}>
                  {disabled ? getMissingRateLabel(option.label) : option.label}
                </MenuItem>
              );
            })}
          </TextField>
          <TextField
            label="Source"
            select
            value={createForm.source}
            onChange={(e) =>
              setCreateForm((prev) => ({ ...prev, source: e.target.value }))
            }
          >
            <MenuItem value="margin">Margin</MenuItem>
            <MenuItem value="subtotal">Subtotal</MenuItem>
          </TextField>
          <TextField
            label="Rate (%)"
            type="number"
            inputProps={{ min: 0, max: 100, step: 0.01 }}
            value={createForm.ratePercent}
            onChange={(e) =>
              setCreateForm((prev) => ({
                ...prev,
                ratePercent: e.target.value,
              }))
            }
          />
          <TextField
            label="Amount (CA$)"
            value={createForm.amount}
            type="number"
            inputProps={{ step: 0.01 }}
            onChange={(e) => {
              setAmountDirty(true);
              setCreateForm((prev) => ({ ...prev, amount: e.target.value }));
            }}
          />
          {createForm.entryType === "manual" &&
            relatedInvoiceOptions.length > 0 && (
              <TextField
                label="Related invoice"
                select
                value={createForm.relatedInvoiceId || ""}
                onChange={(e) =>
                  setCreateForm((prev) => ({
                    ...prev,
                    relatedInvoiceId: e.target.value,
                  }))
                }
                helperText="Optional context only; manual adjustment amount is not recalculated."
                fullWidth
              >
                <MenuItem value="">Project-level adjustment</MenuItem>
                {relatedInvoiceOptions.map((invoice) => (
                  <MenuItem key={invoice.id} value={String(invoice.id)}>
                    {invoice.invoiceNumber || `INV-${invoice.id}`} —{" "}
                    {currency(invoice.amount)}
                  </MenuItem>
                ))}
              </TextField>
            )}
          <TextField
            label="Note / Reason"
            multiline
            minRows={3}
            value={createForm.note}
            onChange={(e) =>
              setCreateForm((prev) => ({ ...prev, note: e.target.value }))
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={createForm.paid}
                disabled={
                  createBusy || (commissionModalMode === "edit" && createForm.paid)
                }
                onChange={(e) =>
                  setCreateForm((prev) => ({
                    ...prev,
                    paid: e.target.checked,
                  }))
                }
              />
            }
            label={
              commissionModalMode === "edit" && createForm.paid
                ? "Paid — use Unpay before changing payment state"
                : "Mark as paid"
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateOpen(false);
              setEditingCommissionId(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={createCommission}
            disabled={createDisabled}
          >
            {createBusy
              ? "Saving…"
              : commissionModalMode === "edit" && editingCommissionId
                ? "Save Changes"
                : "Add Commission"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* MARK PAID dialog */}
      <Dialog
        open={payOpen}
        onClose={() => setPayOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Mark Commission Paid</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {!summary?.lock ? (
            <Alert severity="warning" sx={{ mb: 1 }}>
              Approve first to lock the rate & snapshot before paying.
            </Alert>
          ) : (
            <Alert severity="success" sx={{ mb: 1 }}>
              Locked at {(Number(summary.lock.rate) * 100).toFixed(1)}% on{" "}
              {new Date(summary.lock.lockedAt).toLocaleString()}.
            </Alert>
          )}

          <TextField
            fullWidth
            label={`Amount (≤ ${currency(maxBalance)})`}
            type="number"
            margin="dense"
            inputProps={{ min: 0, step: "0.01", max: maxBalance }}
            value={payForm.amount}
            error={payForm.kind === "payment" && amtTooHigh}
            helperText={
              payForm.kind === "payment" && amtTooHigh
                ? `Cannot exceed balance ${currency(maxBalance)}.`
                : ""
            }
            onChange={(e) =>
              setPayForm((f) => ({ ...f, amount: e.target.value }))
            }
          />
          <TextField
            fullWidth
            label="Paid At"
            type="date"
            margin="dense"
            InputLabelProps={{ shrink: true }}
            value={payForm.paidAt}
            onChange={(e) =>
              setPayForm((f) => ({ ...f, paidAt: e.target.value }))
            }
          />
          <TextField
            fullWidth
            label="Reference (optional)"
            margin="dense"
            value={payForm.reference}
            onChange={(e) =>
              setPayForm((f) => ({ ...f, reference: e.target.value }))
            }
          />
          <TextField
            select
            fullWidth
            label="Kind"
            margin="dense"
            value={payForm.kind}
            onChange={(e) =>
              setPayForm((f) => ({ ...f, kind: e.target.value }))
            }
          >
            <MenuItem value="payment">Payment</MenuItem>
            <MenuItem value="adjustment">Adjustment</MenuItem>
          </TextField>
          <TextField
            select
            fullWidth
            label="Recipient"
            margin="dense"
            value={payForm.repUserId}
            onChange={(e) =>
              setPayForm((f) => ({ ...f, repUserId: e.target.value }))
            }
            helperText="Optional: choose a specific rep recipient; leave blank to use default/non-recipient-specific entry"
          >
            <MenuItem value="">Default / Unspecified</MenuItem>
            {repOptions.map((repOption) => (
              <MenuItem
                key={`pay-rep-${repOption.id}`}
                value={String(repOption.id)}
              >
                {repOption.name} — {repOption.roles}
              </MenuItem>
            ))}
          </TextField>
          <Typography
            variant="caption"
            sx={{ mt: 0.5, display: "block", opacity: 0.75 }}
          >
            Balance: {currency(balance)} &nbsp; • &nbsp; Due:{" "}
            {currency(commissionDue)} &nbsp; • &nbsp; Paid: {currency(paid)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={doPay} disabled={saveDisabled}>
            {payBusy ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  </Paper>
);
}
